<script setup lang="ts">
import { ref } from "vue";
import { useRouter } from "vue-router";
import { useDark } from "@vueuse/core";
import { tokenManager } from "../../../api/utils/tokenManager";
import { useAuthStore } from "../../../stores/auth";
import { useAppTour } from "../../../composables/useAppTour";
import IconComponent from "../../ui/common/IconComponent.vue";
import SwitchInput from "../../ui/form/SwitchInput.vue";
import AsyncImage from "../../ui/form/AsyncImage.vue";
import { Company } from "../../../types/auth";
import { api } from "../../../api/Api";
import MessageBox from "../../../services/MessageBox";

const isDark = useDark();
const toggleTheme = (dark: boolean) => {
  if (dark) {
    tokenManager.setTheme("dark");
    isDark.value = true;
  } else {
    tokenManager.setTheme("light");
    isDark.value = false;
  }
};

const isOpen = ref(false);
const store = useAuthStore();
const router = useRouter();
const { startTour } = useAppTour();

const navigateTo = (path: string) => {
  router.push(path)
  isOpen.value = false
}

const handleLogout = async () => {
  await store.logout();
  isOpen.value = false;
};

const handleSelect = async (company: Company) => {
  const apiData = {
    cid: company.uid,
    email: tokenManager.getUser()?.email,
    password: tokenManager.getPassword() || "",
  };
  const response = await api.post("/company", apiData);
  if (response.success == true) {
    tokenManager.setMenu(response.data.menu);
    tokenManager.setPermissions(response.data.permissions);
    tokenManager.setSettings(response.data.settings);
    tokenManager.setCompany(company);
    tokenManager.setUser(response.data.user);
    const user = response.data.user;
    if (response.data.welcomed && user?.user_type == "Back Office") {
      window.location.href = "/dashboard";
    } else if (user?.user_type == "POS User") {
      window.location.href = "/pos";
    } else if (user?.user_type == "Delivery") {
      window.location.href = "/delivery-trackings";
    } else if (user?.user_type == "Sales Rep") {
      window.location.href = "/reps";
    } else if (!response.data.welcomed && user?.user_type == "Back Office") {
      window.location.href = "/welcome";
    } else {
      MessageBox.show(
        "Please contact your administrator to set up your account."
      );
    }
  }
};

</script>

<template>
  <div class="relative">
    <!-- Profile Button -->
    <button
      @click="isOpen = !isOpen"
      v-click-outside="() => (isOpen = false)"
      class="flex items-center gap-2 focus:outline-none"
    >
      <div
        class="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center text-blue-600 dark:text-blue-400"
      >
        <IconComponent name="UserCircle" class="w-6 h-6" />
      </div>
    </button>

    <!-- Dropdown Menu -->
    <div
      v-if="isOpen"
      class="absolute right-0 mt-2 w-64 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-2 z-[1000]"
    >
      <!-- User Info -->
      <div class="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
        <div class="font-medium text-gray-900 dark:text-white">
          {{ tokenManager.getUser()?.name }}
        </div>
        <div class="text-sm text-gray-500 dark:text-gray-400">
          {{ tokenManager.getUser()?.email }}
        </div>
      </div>

      <!-- Company Info -->
      <div class="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
        <div class="font-medium text-gray-900 dark:text-white">
          {{ tokenManager.getCompany()?.company_name }}
        </div>
        <div class="text-sm text-gray-500 dark:text-gray-400">
          {{ tokenManager.getCompany()?.license_expiry }}
        </div>
      </div>

      <div
        class="px-4 py-3 border-b border-gray-200 dark:border-gray-700"
        v-if="tokenManager.getCompanyList()?.length > 1"
      >
        <div class="relative group">
          <button
            class="flex items-center gap-3 px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <span>Switch Company</span>
            <IconComponent name="ChevronRight" class="ml-auto w-4 h-4" />
          </button>
            <div
            class="absolute left-[-100%] top-0 hidden group-hover:block w-64 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-2 z-[1000] max-h-64 overflow-y-auto"
            >
            <template
              v-for="company in tokenManager.getCompanyList()"
              :key="company.uid"
            >
              <button
              @click="handleSelect(company)"
              class="flex items-center gap-3 px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
              <div class="w-10 h-10">
                <AsyncImage
                v-if="company.logo"
                :readonly="true"
                :src="company.logo"
                alt="Company Logo"
                />
              </div>
              <div>
                <h3 class="font-medium text-gray-900 dark:text-white">
                {{ company.company_name }}
                </h3>
                <p class="text-sm text-gray-500 dark:text-gray-400">
                License expires:
                {{ new Date(company.license_expiry).toLocaleDateString() }}
                </p>
              </div>
              </button>
            </template>
            </div>
        </div>
      </div>
      <!-- Menu Items -->
      <div class="py-1">
        <button
          @click="navigateTo('/settings')"
          class="w-full px-4 py-2 flex text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
        >
          <IconComponent name="Settings" class="mr-2" /> My Settings
        </button>
        <button
          @click="navigateTo('/profile')"
          class="w-full px-4 py-2 flex text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
        >
          <IconComponent name="UserRound" class="mr-2" /> My Profile
        </button>
        <button
          @click="startTour"
          class="w-full px-4 py-2 flex text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
        >
          <IconComponent name="Presentation" class="mr-2" /> Quick Tour
        </button>
        <SwitchInput
          label="Dark Theme"
          v-model="isDark"
          class="ml-[15px] mb-1"
          @click.stop
          @change="(e: any) => { e.stopPropagation(); toggleTheme(e.target.checked); }"
        />
      </div>

      <!-- Separator -->
      <div class="border-t border-gray-200 dark:border-gray-700"></div>

      <!-- Logout -->
      <div class="py-1">
        <button
          @click="navigateTo('/change-password')"
          class="w-full px-4 py-2 flex text-left text-sm text-orange-600 dark:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/50"
        >
          <IconComponent name="KeyRound" class="mr-2" /> Change Password
        </button>
        <button
          @click="handleLogout"
          class="w-full px-4 py-2 flex text-left text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/50"
        >
          <IconComponent name="LogOut" class="mr-2" /> Log Out
        </button>
      </div>
    </div>
  </div>
</template>
